import React from "react"
import Link from "next/link"
import {Trans, Plural} from "@lingui/react/macro"
import styles from "./styles.module.css"
import Recommendations from "./Recommendations"
import RecommendationsSidebar from "./RecommendationsSidebar"
import SmartImage from "./SmartImage"
import {buildCategoryUrl, buildHomeUrl, pickThumbnail, rewriteArticleHtml} from "./utils"

export default function Article({context, article, recommendations, breadcrumbAids = []}) {
    if (!article) {
        return (
            <div className={styles.containerNarrow}>
                <div className={styles.empty}>
                    <div className={styles.emptyIcon}>🔎</div>
                    <h2><Trans>Article introuvable</Trans></h2>
                    <p><Trans>L'article demandé n'existe pas ou n'est plus disponible.</Trans></p>
                    <p style={{marginTop: 16}}>
                        <Link href={buildHomeUrl(context)} passHref legacyBehavior>
                            <a className={styles.sectionLink}>
                                <Trans>← Retour à la bibliothèque</Trans>
                            </a>
                        </Link>
                    </p>
                </div>
            </div>
        )
    }

    const breadcrumb = Array.isArray(article.breadcrumb) ? article.breadcrumb : []
    const crumbs = breadcrumb.map((label, idx) => ({
        label,
        aid: breadcrumbAids[idx]?.aid || null,
    }))
    const heroImage = pickThumbnail(article)
    const html = rewriteArticleHtml(article.body_html || "")
    const readingMin = article.word_count > 0 ? Math.max(1, Math.round(article.word_count / 200)) : null

    const sidebarItems = recommendations.slice(0, 5)
    const stripItems = recommendations

    return (
        <>
            <div className={styles.articleLayout}>
                <article className={styles.articleMain}>
                    {context.render !== "embed" && <nav className={styles.breadcrumb}>
                        <Link href={buildHomeUrl(context)} passHref legacyBehavior>
                            <a className={styles.breadcrumbLink}><Trans>Explorer la bibliothèque</Trans></a>
                        </Link>
                        {crumbs.map((c, idx) => (
                            <React.Fragment key={idx}>
                                <span className={styles.breadcrumbSep}>›</span>
                                {c.aid ? (
                                    <Link href={buildCategoryUrl(context, c.aid)} passHref legacyBehavior>
                                        <a className={styles.breadcrumbLink}>{c.label}</a>
                                    </Link>
                                ) : (
                                    <span className={styles.breadcrumbCurrent}>{c.label}</span>
                                )}
                            </React.Fragment>
                        ))}
                    </nav>}

                    <header className={styles.articleHero}>
                        {breadcrumb.length > 0 && (
                            <div className={styles.articleHeroEyebrow}>
                                {breadcrumb.slice(-1)[0]}
                            </div>
                        )}
                        <h1 className={styles.articleHeroTitle}>{article.title}</h1>
                        {article.description && (
                            <p className={styles.articleHeroDesc}>{article.description}</p>
                        )}
                        <div className={styles.articleHeroMeta}>
                            {article.author && <span>👤 {article.author}</span>}
                            {readingMin && (
                                <span>
                                    ⏱ <Plural value={readingMin} one="# min de lecture" other="# min de lecture"/>
                                </span>
                            )}
                        </div>
                    </header>

                    {heroImage && (
                        <SmartImage
                            src={heroImage}
                            alt={article.title || ""}
                            loading="eager"
                            style={{
                                width: "100%",
                                maxHeight: 480,
                                objectFit: "cover",
                                borderRadius: "var(--ch-radius)",
                                marginBottom: 32,
                                display: "block",
                            }}
                        />
                    )}

                    <div
                        className={styles.articleBody}
                        dangerouslySetInnerHTML={{__html: html}}
                    />

                    {article.url_source && (
                        <div style={{marginTop: 32, paddingTop: 16, borderTop: "1px solid var(--ch-border)", fontSize: 13, color: "var(--ch-text-mute)"}}>
                            <a href={article.url_source} target="_blank" rel="noopener noreferrer" style={{color: "inherit"}}>
                                <Trans>Source</Trans> ↗
                            </a>
                        </div>
                    )}
                </article>

                <RecommendationsSidebar
                    context={context}
                    items={sidebarItems}
                    className={styles.hideOnMobile}
                />
            </div>

            <div className={styles.showOnMobileOnly}>
                <Recommendations
                    context={context}
                    items={stripItems}
                />
            </div>
        </>
    )
}
